import { AppBadge } from '@/shared/components/app-guide/AppBadge';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogTitle,
} from '@/shared/components/ui/alert-dialog';
import { Button } from '@/shared/components/ui/button';
import { getApiErrorCode, isNetworkError } from '@/shared/api/http-client';
import { ROUTE_PATHS } from '@/shared/constants/routes';
import { cn } from '@/shared/lib/utils';
import { useAuthStore } from '@/stores/authStore';
import { rememberLastProvider } from '@/pages/login/components/SocialLoginButtons';
import { zodResolver } from '@hookform/resolvers/zod';
import { useNavigate } from '@tanstack/react-router';
import { Eye, EyeOff } from 'lucide-react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

const loginSchema = z.object({
  email: z.string().email('올바른 형식의 이메일 주소를 입력해주세요.'),
  password: z.string().min(8, '비밀번호는 8자 이상 입력해주세요.'),
});
type LoginValues = z.infer<typeof loginSchema>;

/** M-02 실패 문구 — 없는 이메일 · 틀린 비밀번호 · 탈퇴 계정을 구분하지 않는다 */
const AUTH_FAILED = '이메일 또는 비밀번호가 올바르지 않아요.';
const BLOCKED = '시도 횟수를 초과했어요. 잠시 후 다시 시도해주세요.';
const NETWORK = '연결이 불안정해요. 잠시 후 다시 시도해주세요.';

/** M-03 X4 · X5 — 로그인 화면은 계정이 없는 사람도 보므로 문구를 한 줄 더 적는다 */
const APP_GUIDE = {
  signup: {
    title: '회원가입은 앱에서 할 수 있어요',
    body: '루다링크 앱에서 가입한 뒤 같은 계정으로 로그인해주세요.',
  },
  reset: {
    title: '비밀번호 재설정은 앱에서만 가능해요',
    body: '휴대폰 본인인증이 필요해 앱에서만 진행합니다.',
  },
} as const;

const fieldBox =
  'flex h-[52px] items-center gap-2 rounded-md border bg-surface px-4 focus-within:border-ink';

export const LoginForm = ({
  disabled,
  redirectTo,
}: {
  disabled: boolean;
  /** 로그인 전에 들어오려던 내부 주소 — 성공하면 그리로 돌려보낸다 */
  redirectTo?: string;
}) => {
  const navigate = useNavigate();
  const login = useAuthStore((s) => s.login);
  const [showPassword, setShowPassword] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [blocked, setBlocked] = useState(false);
  const [guide, setGuide] = useState<keyof typeof APP_GUIDE | null>(null);

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<LoginValues>({
    resolver: zodResolver(loginSchema),
    mode: 'onBlur', // 형식 오류는 포커스 아웃 시점에 표시 (S3)
  });

  const [email, password] = watch(['email', 'password']);
  const filled = !!email && !!password;
  // 형식 오류 문구는 포커스 아웃 때 보여주지만, 버튼은 입력할 때마다 같은 스키마로 판정해
  // 형식이 맞지 않으면 비활성으로 둔다 (이메일 형식 · 비밀번호 8자 이상)
  const valid = loginSchema.safeParse({ email, password }).success;
  const hasAuthError = submitError === AUTH_FAILED;

  const onSubmit = async (values: LoginValues) => {
    setSubmitError(null);
    try {
      await login(values.email, values.password);
      rememberLastProvider('email');
      if (redirectTo) {
        void navigate({ href: redirectTo, replace: true });
      } else {
        void navigate({ to: ROUTE_PATHS.ACCOUNTS, replace: true });
      }
    } catch (error) {
      if (isNetworkError(error)) return setSubmitError(NETWORK);
      if (getApiErrorCode(error) === 'LOGIN_ATTEMPTS_EXCEEDED') {
        setBlocked(true);
        return setSubmitError(BLOCKED);
      }
      setSubmitError(AUTH_FAILED);
    }
  };

  return (
    <>
      <form
        onSubmit={handleSubmit(onSubmit)}
        className="flex flex-col"
        noValidate
      >
        <fieldset disabled={disabled} className="flex flex-col gap-[18px]">
          <label className="flex flex-col gap-2">
            <span className="text-[13px] leading-5 font-medium text-ink-2">
              이메일
            </span>
            <div
              className={cn(
                fieldBox,
                errors.email || hasAuthError
                  ? 'border-danger'
                  : 'border-line-strong',
              )}
            >
              <input
                type="email"
                autoComplete="email"
                placeholder="email@rudalink.com"
                className="w-full bg-transparent text-[15px] leading-[23px] outline-none placeholder:text-ink-8"
                {...register('email', {
                  // 차단은 이메일 기준이라 이메일을 바꾸면 차단 표시도 푼다
                  onChange: () => {
                    setBlocked(false);
                    setSubmitError(null);
                  },
                })}
              />
            </div>
            {errors.email && (
              <span className="text-xs text-danger">
                {errors.email.message}
              </span>
            )}
          </label>

          <label className="flex flex-col gap-2">
            <span className="text-[13px] leading-5 font-medium text-ink-2">
              비밀번호
            </span>
            <div
              className={cn(
                fieldBox,
                errors.password || hasAuthError
                  ? 'border-danger'
                  : 'border-line-strong',
              )}
            >
              <input
                type={showPassword ? 'text' : 'password'}
                // 비밀번호 자동완성은 브라우저 비밀번호 관리자에 맡긴다
                autoComplete="current-password"
                placeholder="비밀번호를 입력해주세요"
                className="w-full bg-transparent text-[15px] leading-[23px] outline-none placeholder:text-ink-8"
                {...register('password', {
                  // 차단 중에는 사유 문구를 남겨 둔다 (버튼이 왜 막혔는지 보이도록)
                  onChange: () => !blocked && setSubmitError(null),
                })}
              />
              <button
                type="button"
                onClick={() => setShowPassword((v) => !v)}
                className="text-ink-7"
                aria-label={showPassword ? '비밀번호 숨기기' : '비밀번호 보기'}
              >
                {showPassword ? (
                  <EyeOff className="size-4" />
                ) : (
                  <Eye className="size-4" />
                )}
              </button>
            </div>
            {errors.password && (
              <span className="text-xs text-danger">
                {errors.password.message}
              </span>
            )}
          </label>
        </fieldset>

        {submitError && (
          <p className="mt-3 text-xs text-danger" role="alert">
            {submitError}
          </p>
        )}

        <Button
          type="submit"
          size="xl"
          loading={isSubmitting}
          disabled={!filled || !valid || blocked || disabled}
          className="mt-6 h-[54px] text-base disabled:bg-[#c9c9c9] disabled:opacity-100"
        >
          로그인
        </Button>
        <p className="mt-2.5 text-center text-xs leading-[18px] text-ink-7">
          두 항목을 모두 입력하면 버튼이 활성화됩니다.
        </p>

        <div className="mt-6 flex items-center justify-center gap-4">
          <button
            type="button"
            onClick={() => setGuide('reset')}
            className="flex items-center gap-1.5 text-[13px] leading-5 text-ink-4"
          >
            비밀번호 재설정
            <AppBadge />
          </button>
          <span className="h-3 w-px bg-[#ddd]" />
          <button
            type="button"
            onClick={() => setGuide('signup')}
            className="flex items-center gap-1.5 text-[13px] leading-5 text-ink-4"
          >
            회원가입
            <AppBadge />
          </button>
        </div>
      </form>

      <AlertDialog
        open={guide !== null}
        onOpenChange={(o) => !o && setGuide(null)}
      >
        <AlertDialogContent>
          {guide && (
            <>
              <AlertDialogTitle>{APP_GUIDE[guide].title}</AlertDialogTitle>
              <AlertDialogDescription>
                {APP_GUIDE[guide].body}
              </AlertDialogDescription>
            </>
          )}
          <AlertDialogFooter>
            <AlertDialogAction>확인</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
